import * as trace from 'owa-trace';
import getGlobalErrorState from '../store/Store';
import React, { Suspense } from 'react';
import { createLazyComponent } from 'owa-bundling';
import { observer } from 'mobx-react';
import { ErrorConfig, ErrorHandlerConfigType } from '../schema/ErrorConfig';
import { ErrorScreenProps } from '../schema/ErrorScreen.types';

interface ErrorBoundaryState {
    isError: boolean;
}

/**
 * @description: Higher Order Component to take care of
 * 1. React Render Tree failures using error boundaries.
 * 2. Suspense load failure, when passed a lazy/suspense component.
 * 3. LazyComponent failure. Uses OWA bundling createLazyComponent component.
 * Pending: add usage docs: https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/89351
 */
export function getBaseErrorHandler<TProps, TModule, TInnerRef, TPlaceHolderRef = void>(
    ErrorScreen: React.ComponentType<ErrorScreenProps>,
    WrappedComponent:
        | React.ComponentType<TProps>
        | React.LazyExoticComponent<(props: TProps) => JSX.Element>
        | React.LazyExoticComponent<React.ComponentType<any>>
        | null,
    errorConfig: ErrorConfig<TProps, TModule, TInnerRef, TPlaceHolderRef>
): React.ComponentType<TProps> {
    @observer
    class ErrorBoundary extends React.Component<TProps, ErrorBoundaryState> {
        constructor(props: TProps) {
            super(props);
            this.state = { isError: false };
        }

        componentDidCatch(error: trace.TraceErrorObject, errorInfo: React.ErrorInfo) {
            this.setState({ isError: true });
            this.logComponentRenderFailure(error, errorInfo);
        }

        /**
         * logs the failure to aria
         * errors will be bunched with "COMPONENT ERROR",
         * the number of failures overall are low now,
         * but can raise error if something fails miserably
         */
        private logComponentRenderFailure(
            error: trace.TraceErrorObject,
            errorInfo: React.ErrorInfo
        ) {
            error.component = true;
            error.diagnosticInfo = this.createDiagnosticInfo(error, errorInfo);
            trace.errorThatWillCauseAlert(error);
        }

        /**
         * If the render tree failed while loading a suspense component
         * the Error Stack will be bunched together with 'SUSPENSE FAILURE'
         */
        private createDiagnosticInfo(error: trace.TraceErrorObject, errorInfo: React.ErrorInfo) {
            const { diagnosticInfo = '' } = error;
            const failureReason =
                errorConfig.configType === ErrorHandlerConfigType.suspense
                    ? 'SUSPENSE FAILURE'
                    : '';
            const { componentStack } = errorInfo;

            return `${diagnosticInfo} ${failureReason} ${componentStack}`;
        }

        private onLazyLoadError(importErrorHandler: (failureReason: any) => void) {
            return (reason: any) => {
                importErrorHandler?.(reason); // call the functions passed in config
                this.setState({ isError: true });
                /**
                 * This does need logging as the LazyModule is already logging for these errors
                 * See: client-web/shared/internal/owa-bundling-light/src/LazyModule.ts > onLoadFailed
                 * These errors are bunched together with 'Failed to load LazyModule' message
                 */
            };
        }

        /**
         * There will be multiple instances of this Higher Order Component.
         * We need to trigger the Error Screen only for the component which is configured
         */
        private isGlobalErrorForCurrentHandler() {
            const { errorSource, globalError } = getGlobalErrorState;
            if (errorConfig.configType === ErrorHandlerConfigType.simple) {
                return errorSource === errorConfig.errorSource && globalError;
            }
            return false;
        }

        render() {
            if (this.state.isError || this.isGlobalErrorForCurrentHandler()) {
                return <ErrorScreen {...(errorConfig.errorScreen as any)} />;
            }

            switch (errorConfig.configType) {
                case ErrorHandlerConfigType.suspense:
                    const { SuspenseFallBackComponent } = errorConfig;
                    return (
                        <Suspense
                            fallback={SuspenseFallBackComponent && <SuspenseFallBackComponent />}>
                            <WrappedComponent {...(this.props as any)} />
                        </Suspense>
                    );

                case ErrorHandlerConfigType.simple:
                    return <WrappedComponent {...(this.props as any)} />;

                case ErrorHandlerConfigType.owaLazy:
                    const {
                        lazyModule,
                        componentGetter,
                        placeholder = null,
                        importErrorHandler: errorCb,
                    } = errorConfig;

                    const errorHandlerPartial = this.onLazyLoadError(errorCb);
                    const Lazy = createLazyComponent(
                        lazyModule,
                        componentGetter,
                        placeholder as any,
                        errorHandlerPartial
                    );
                    return <Lazy {...(this.props as any)} />;
            }
        }
    }
    return ErrorBoundary;
}
